import { useEffect, useState } from 'react';
import { Plus, Trash2, FileText, Search, FolderOpen, RefreshCw, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { moduleService, Module } from '@/services/moduleService';
import { useClientDocumentsConfig } from '@/hooks/useClientDocumentsConfig';
import { isElectronEnvironment } from '@/hooks/useElectronFileSystem';
import { normalizeDocumentPath } from '@/utils/normalizeDocumentPath';
import { joinUncDocumentsPath } from '@/utils/joinUncPath';
import { toast } from 'sonner';

const MODULES_SUBFOLDER = 'ModuliStudioGori';

function getMimeIcon(fileName: string) {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) return 'PDF';
  if (lower.endsWith('.doc') || lower.endsWith('.docx')) return 'DOC';
  if (lower.endsWith('.xls') || lower.endsWith('.xlsx')) return 'XLS';
  return 'FILE';
}

export default function ModulesPage() {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [moduleName, setModuleName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const { documentsNasRoot } = useClientDocumentsConfig();
  const inElectron = isElectronEnvironment();

  const modulesNasPath = (() => {
    const root = normalizeDocumentPath(documentsNasRoot || '');
    if (!root) return '';
    return joinUncDocumentsPath(root, MODULES_SUBFOLDER);
  })();

  const loadModules = async () => {
    setLoading(true);
    try {
      const data = await moduleService.getModules();
      setModules(data);
    } catch {
      toast.error('Errore nel caricamento dei moduli');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  const handleUpload = async () => {
    if (!moduleName.trim()) {
      toast.error('Inserisci un nome per il modulo');
      return;
    }
    if (!selectedFile) {
      toast.error('Seleziona un file');
      return;
    }

    setUploading(true);
    try {
      if (inElectron && window.electronFS && modulesNasPath) {
        if (!window.electronFS.directoryExists(modulesNasPath)) {
          window.electronFS.createDirectory(modulesNasPath);
        }

        const destPath = window.electronFS.joinPath(modulesNasPath, selectedFile.name);
        const arrayBuffer = await selectedFile.arrayBuffer();
        window.electronFS.writeFile(destPath, arrayBuffer);

        await moduleService.syncNasModules([selectedFile.name]);
        toast.success(`Modulo "${moduleName}" caricato su NAS e registrato`);
      } else {
        const formData = new FormData();
        formData.append('name', moduleName);
        formData.append('file', selectedFile);
        formData.append('nasRelativePath', selectedFile.name);
        await moduleService.createModule(formData);
        toast.success(`Modulo "${moduleName}" creato`);
      }

      resetDialog();
      loadModules();
    } catch (err: any) {
      toast.error(err.message || 'Errore nel caricamento del modulo');
    } finally {
      setUploading(false);
    }
  };

  const handlePickFileFromNas = () => {
    if (!inElectron || !window.electronDialog) return;
    window.electronDialog.pickDirectory(modulesNasPath || undefined).then(() => {
      // Dialog closed; not needed for this flow
    }).catch(() => {});
  };

  const handleDelete = async (mod: Module) => {
    if (!window.confirm(`Sei sicuro di voler rimuovere il modulo "${mod.name}"?`)) return;
    try {
      if (inElectron && window.electronFS && modulesNasPath && mod.nasRelativePath) {
        const filePath = window.electronFS.joinPath(modulesNasPath, mod.nasRelativePath);
        if (window.electronFS.pathExists(filePath)) {
          window.electronFS.deleteEntry(filePath);
        }
      }
      await moduleService.deleteModule(mod.id);
      toast.success('Modulo eliminato');
      loadModules();
    } catch {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleOpenOnNas = (mod: Module) => {
    if (!inElectron || !window.electronFS || !modulesNasPath || !mod.nasRelativePath) return;
    const fullPath = window.electronFS.joinPath(modulesNasPath, mod.nasRelativePath);
    window.electronFS.openPath(fullPath).catch(() => {
      toast.error('Impossibile aprire il file');
    });
  };

  const handleSyncNas = async () => {
    if (!inElectron || !window.electronFS || !modulesNasPath) return;
    try {
      if (!window.electronFS.directoryExists(modulesNasPath)) {
        toast.info('Cartella ModuliStudioGori non trovata sul NAS');
        return;
      }
      const entries = window.electronFS.readDirectory(modulesNasPath);
      const files = entries
        .filter(e => e.kind === 'file')
        .map(e => e.name)
        .filter(n => /\.(pdf|docx?|xlsx?)$/i.test(n));

      if (files.length === 0) {
        toast.info('Nessun file modulo nella cartella NAS');
        return;
      }

      const result = await moduleService.syncNasModules(files);
      setModules(result.data);
      if (result.created > 0) {
        toast.success(`${result.created} nuovi moduli sincronizzati`);
      } else {
        toast.info('Tutti i moduli sono già sincronizzati');
      }
    } catch {
      toast.error('Errore nella sincronizzazione');
    }
  };

  const resetDialog = () => {
    setShowUploadDialog(false);
    setModuleName('');
    setSelectedFile(null);
  };

  const filteredModules = modules.filter(m =>
    !searchTerm ||
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.originalName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.activityType?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !modules.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento moduli...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Moduli</h1>
          <p className="mt-1 text-sm text-gray-500">
            Template documenti salvati in <span className="font-mono text-xs">{MODULES_SUBFOLDER}</span> sul NAS
          </p>
        </div>
        <div className="flex gap-2">
          {inElectron && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  if (modulesNasPath && window.electronFS) {
                    window.electronFS.openPath(modulesNasPath).catch(() => {
                      toast.error('Impossibile aprire la cartella NAS');
                    });
                  }
                }}
                disabled={!modulesNasPath}
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Apri cartella
              </Button>
              <Button variant="outline" onClick={handleSyncNas} disabled={!modulesNasPath}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Sincronizza
              </Button>
            </>
          )}
          <Button onClick={() => setShowUploadDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Modulo
          </Button>
        </div>
      </div>

      {!inElectron && (
        <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-4 py-3">
          Per caricare i moduli direttamente nella cartella NAS condivisa, usa il client desktop Studio Gori.
          Dal browser puoi comunque caricare moduli sul server.
        </div>
      )}

      {modules.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Cerca per nome o file..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      <div className="grid gap-3">
        {filteredModules.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'Nessun risultato' : 'Nessun modulo presente'}
              </p>
              {!searchTerm && (
                <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Carica il primo modulo
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredModules.map((mod) => (
            <Card key={mod.id} className="hover:shadow-md transition-shadow">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline" className="font-mono text-xs">
                      {getMimeIcon(mod.nasRelativePath || mod.originalName)}
                    </Badge>
                    <div>
                      <h3 className="font-semibold">{mod.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className="font-mono text-xs">{mod.nasRelativePath || mod.originalName}</span>
                        {mod.activityType && (
                          <>
                            <span>—</span>
                            <span>{mod.activityType.icon} {mod.activityType.name}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {inElectron && mod.nasRelativePath && (
                      <Button variant="outline" size="sm" onClick={() => handleOpenOnNas(mod)}>
                        <FolderOpen className="h-4 w-4 mr-1" />
                        Apri
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={() => handleDelete(mod)} className="text-red-600 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={showUploadDialog} onOpenChange={(open) => { if (!open) resetDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Modulo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome modulo *</Label>
              <Input
                value={moduleName}
                onChange={(e) => setModuleName(e.target.value)}
                placeholder="Es: Dichiarazione di conformità"
              />
            </div>
            <div>
              <Label>File template *</Label>
              <Input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setSelectedFile(file);
                  if (file && !moduleName.trim()) {
                    const ext = file.name.lastIndexOf('.');
                    setModuleName(ext > 0 ? file.name.substring(0, ext) : file.name);
                  }
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                PDF, Word o Excel (max 50MB).
                {inElectron
                  ? ' Il file verrà copiato nella cartella ModuliStudioGori sul NAS.'
                  : ' Il file verrà caricato sul server.'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDialog}>Annulla</Button>
            <Button onClick={handleUpload} disabled={!moduleName.trim() || !selectedFile || uploading}>
              {uploading ? 'Caricamento...' : 'Carica'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
